import { useCallback } from 'react';
import { TourStep } from './usePageTour';
import { usePageTour } from './usePageTour';
import { Grid } from 'antd';
import { useTranslation } from 'react-i18next';

const { useBreakpoint } = Grid;

export interface SimpleCRUDTourConfig {
  pageName: string;
  recordType: string; // e.g., 'asset', 'employee', 'vendor', 'resident'
  hasRecords: boolean;
  recordCount: number;
  firstRecordId?: string;
  additionalSteps?: TourStep[];
  onAddRecord?: () => void;
  onViewRecord?: (recordId: string) => void;
  onEditRecord?: (recordId: string) => void;
  onDeleteRecord?: (recordId: string) => void;
}

export const useSimpleCRUDTour = () => {
  const { startTour, stopTour, cleanup, isActive } = usePageTour();
  const screens = useBreakpoint();
  const { t } = useTranslation('common');

  // Helper function to get record type plural
  const getRecordTypePlural = (recordType: string) => {
    const plurals: Record<string, string> = {
      'asset': 'assets',
      'employee': 'employees', 
      'vendor': 'vendors',
      'resident': 'residents',
      'block': 'blocks',
      'apartment unit': 'apartment units',
      'parking': 'parking spaces',
      'amenity': 'amenities'
    };
    return plurals[recordType] || `${recordType}s`;
  };

  // Wait for elements to be available
  const waitForElements = (selectors: string[], timeout = 5000): Promise<boolean> => {
    return new Promise((resolve) => {
      const startTime = Date.now();
      
      const checkElements = () => {
        const allFound = selectors.every(selector => {
          const element = document.querySelector(selector);
          return element && (element as HTMLElement).offsetParent !== null; // Check if visible
        });
        
        if (allFound) {
          resolve(true);
        } else if (Date.now() - startTime > timeout) {
          resolve(false);
        } else {
          setTimeout(checkElements, 100);
        }
      };
      
      checkElements();
    });
  };

  const generateCRUDSteps = useCallback((config: SimpleCRUDTourConfig): TourStep[] => {
    const { recordType, hasRecords, recordCount, additionalSteps = [] } = config;
    const isMobile = screens.xs;
    const recordTypePlural = getRecordTypePlural(recordType);
    
    const steps: TourStep[] = [];

    // Step 1: Data Table/List
    if (isMobile) {
      // Mobile: List component
      steps.push({
        element: '.ant-list:has(.ant-card)',
        popover: {
          title: t('tour.dataList', { recordTypePlural }),
          description: t('tour.dataListDescription', { recordTypePlural, recordCount }),
          side: 'bottom'
        }
      });
    } else {
      // Desktop: DataTable
      steps.push({
        element: '.data-table-card, .data-table',
        popover: {
          title: t('tour.dataTable', { recordTypePlural }),
          description: t('tour.dataTableDescription', { recordTypePlural, recordCount }),
          side: 'bottom'
        }
      });
    }

    // Step 2: Search Input
    if (isMobile) {
      // Mobile: Search input in List header
      steps.push({
        element: `#${recordType}s-search-input, .search-input`,
        popover: {
          title: t('tour.searchRecords'),
          description: t('tour.searchRecordsDescription'),
          side: 'bottom'
        }
      });
    } else {
      // Desktop: Search input in table actions
      steps.push({
        element: `#${recordType}s-search-input, .search-input`,
        popover: {
          title: t('tour.searchRecords'),
          description: t('tour.searchRecordsDescription'),
          side: 'bottom'
        }
      });
    }

    // Step 3: Add Record Button
    if (isMobile) {
      // Mobile: Add button in List header
      steps.push({
        element: `#${recordType}s-add-button, .add-record-button`,
        popover: {
          title: t('tour.addNewRecord'),
          description: t('tour.addNewRecordDescription', { recordType }),
          side: 'bottom'
        }
      });
    } else {
      // Desktop: Add button in table actions
      steps.push({
        element: `#${recordType}s-add-button, .add-record-button`,
        popover: {
          title: t('tour.addNewRecord'),
          description: t('tour.addNewRecordDescription', { recordType }),
          side: 'bottom'
        }
      });
    }

    // Step 4: Record Actions (only if records exist)
    if (hasRecords && recordCount > 0) {
      if (isMobile) {
        // Mobile: Action buttons in Card actions
        steps.push({
          element: '.ant-card-actions',
          popover: {
            title: t('tour.recordActions'),
            description: t('tour.recordActionsDescription', { recordType }),
            side: 'left'
          }
        });
      } else {
        // Desktop: Action buttons in record-actions class
        steps.push({
          element: '.record-actions',
          popover: {
            title: t('tour.recordActions'),
            description: t('tour.recordActionsDescription', { recordType }),
            side: 'left'
          }
        });
      }
    }

    // Step 5: Pagination (only if records exist)
    if (hasRecords && recordCount > 0) {
      if (isMobile) {
        // Mobile: Pagination below List
        steps.push({
          element: '.pagination-section, .ant-pagination',
          popover: {
            title: t('tour.pagination'),
            description: t('tour.paginationDescription'),
            side: 'top'
          }
        });
      } else {
        // Desktop: Pagination in DataTable
        steps.push({
          element: '.ant-pagination',
          popover: {
            title: t('tour.pagination'),
            description: t('tour.paginationDescription'),
            side: 'top'
          }
        });
      }
    }

    // Add any additional steps
    steps.push(...additionalSteps);

    return steps;
  }, [screens.xs, t]);

  const startCRUDTour = useCallback(async (config: SimpleCRUDTourConfig) => {
    try {
      // Clean up any existing tour
      cleanup();
      
      // Wait a bit for cleanup to complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const steps = generateCRUDSteps(config);
      
      // Wait for key elements to be available
      const keySelectors = [
        '.data-table-card, .data-table, .ant-list:has(.ant-card)',
        `#${config.recordType}s-search-input, .search-input`,
        `#${config.recordType}s-add-button, .add-record-button`
      ];
      
      const elementsReady = await waitForElements(keySelectors, 3000);
      
      if (!elementsReady) {
        console.warn('Some tour elements not found, starting tour anyway...');
      }
      
      startTour(steps);
    } catch (error) {
      console.error('Error starting CRUD tour:', error);
    }
  }, [generateCRUDSteps, startTour, cleanup]);

  return {
    startCRUDTour,
    stopTour,
    cleanup,
    isActive
  };
};
